import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  TableSortLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Security,
  People,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { User } from '../types';

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RoleFormData {
  name: string;
  description: string;
  permissions: string[];
  isActive: boolean;
}

const RolesManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissions: [],
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<
    'name' | 'description' | 'users' | 'permissions' | 'status' | 'created'
  >('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');


  const availablePermissions = [
    'users.create',
    'users.read',
    'users.update',
    'users.delete',
    'departments.create',
    'departments.read',
    'departments.update',
    'departments.delete',
    'campaigns.create',
    'campaigns.read',
    'campaigns.update',
    'campaigns.delete',
    'schedules.create',
    'schedules.read',
    'schedules.update',
    'schedules.delete',
    'time_entries.create',
    'time_entries.read',
    'time_entries.update',
    'time_entries.delete',
    'reports.read',
    'reports.export',
    'dashboard.read',
    'settings.read',
    'settings.update',
    'activity_logs.read',
    'absences.create',
    'absences.read',
    'absences.update',
    'absences.delete',
    'coverage_assignments.create',
    'coverage_assignments.read',
    'coverage_assignments.update',
    'coverage_assignments.delete',
    'operations.attendance_coverage.read',
    'operations.attendance_coverage.notify',
    'operations.attendance_coverage.cross_campaign_read',
    'operations.attendance_coverage.cross_campaign_assign',
    'operations.attendance_coverage.export',
    'time_adjustments.read',
    'time_adjustments.review',
    'time_adjustments.export',
    'time_adjustments.self_approve',
    'users.password_recovery.read',
    'users.password_recovery.assist',
    'users.password_recovery.generate_code',
    'monitoring.console.read',
    'monitoring.devices.read',
    'monitoring.diagnostics.read',
    'monitoring.emergency_access.read',
    'monitoring.emergency_access.generate',
    'monitoring.emergency_access.revoke',
    'monitoring.emergency_access.end',
    'monitoring.emergency_access.configure',
    'monitoring.live_wall.read',
    'monitoring.live_screen.view',
    'monitoring.live_screen.configure',
    'monitoring.remote_control.read',
    'monitoring.remote_control.start',
    'monitoring.remote_control.keyboard',
    'monitoring.remote_control.mouse',
    'monitoring.remote_control.fullscreen',
    'monitoring.remote_control.clipboard.send',
    'monitoring.remote_control.clipboard.receive',
    'monitoring.remote_control.chat',
    'monitoring.remote_control.files.upload',
    'monitoring.remote_control.files.download',
    'monitoring.remote_control.actions.execute',
  ];

  const permissionLabels: Record<string, string> = {
    'monitoring.live_wall.read': 'View Workforce View',
    'users.password_recovery.read': 'View Password Recovery Requests',
    'users.password_recovery.assist': 'Assist Password Recovery',
    'users.password_recovery.generate_code': 'Generate Recovery Codes',
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesData, usersData] = await Promise.all([
        apiClient.roles.getAll(),
        apiClient.users.getAll(),
      ]);
      setRoles(rolesData.data.data);
      setUsers(usersData.data.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description,
        permissions: role.permissions,
        isActive: role.isActive,
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        permissions: [],
        isActive: true,
      });
    }
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRole(null);
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingRole) {
        await apiClient.roles.update(editingRole.id, formData);
        setSuccess('Role updated successfully');
      } else {
        await apiClient.roles.create(formData);
        setSuccess('Role created successfully');
      }

      // Close dialog immediately and reload data in background
      handleCloseDialog();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (roleId: string) => {
    if (window.confirm('Are you sure you want to delete this role? This will affect all users assigned to this role.')) {
      try {
        await apiClient.roles.delete(roleId);
        setSuccess('Role deleted successfully');
        await loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete role');
      }
    }
  };

  const getUsersWithRole = (roleName: string) => {
    return users.filter(user => user.role === roleName).length;
  };

  const getSortValue = (role: Role, key: typeof sortBy) => {
    switch (key) {
      case 'name':
        return role.name || '';
      case 'description':
        return role.description || '';
      case 'users':
        return getUsersWithRole(role.name).toString();
      case 'permissions':
        return role.permissions.length.toString();
      case 'status':
        return role.isActive ? 'active' : 'inactive';
      case 'created':
        return role.createdAt || '';
      default:
        return '';
    }
  };

  const handleSortChange = (key: typeof sortBy) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDirection('asc');
  };

  const sortedRoles = [...roles]
    .map((role, index) => ({ role, index }))
    .sort((a, b) => {
      const aValue = getSortValue(a.role, sortBy);
      const bValue = getSortValue(b.role, sortBy);
      const comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base', numeric: true });
      if (comparison !== 0) {
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return a.index - b.index;
    })
    .map(({ role }) => role);


  const handlePermissionChange = (permission: string, checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        permissions: [...formData.permissions, permission]
      });
    } else {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(p => p !== permission)
      });
    }
  };

  const getRoleColor = (roleName: string) => {
    const colors: { [key: string]: string } = {
      super_user: 'error',
      administrator: 'warning',
      manager: 'info',
      supervisor: 'primary',
      team_lead: 'secondary',
      agent: 'success',
      other: 'default',
    };
    return colors[roleName] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
          Roles Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Users may need to log out and log back in for new permissions to apply.
      </Alert>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}


      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('name')}
                    >
                      Role Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'description' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'description'}
                      direction={sortBy === 'description' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('description')}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'users' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'users'}
                      direction={sortBy === 'users' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('users')}
                    >
                      Users
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'permissions' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'permissions'}
                      direction={sortBy === 'permissions' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('permissions')}
                    >
                      Permissions
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'status' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'created' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'created'}
                      direction={sortBy === 'created' ? sortDirection : 'asc'}
                      onClick={() => handleSortChange('created')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Security sx={{ mr: 1, fontSize: 16 }} />
                        <Chip
                          label={role.name.replace('_', ' ').toUpperCase()}
                          color={getRoleColor(role.name) as any}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <People sx={{ mr: 1, fontSize: 16 }} />
                        {getUsersWithRole(role.name)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {role.permissions.length} permissions
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.isActive ? 'Active' : 'Inactive'}
                        color={role.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(role.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(role)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(role.id)}
                        color="error"
                        disabled={getUsersWithRole(role.name) > 0}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Role Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', p: 2 }}>
                {availablePermissions.map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Switch
                        checked={formData.permissions.includes(permission)}
                        onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                        size="small"
                      />
                    }
                    label={permissionLabels[permission] || permission}
                  />
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Saving...' : (editingRole ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default RolesManagementPage;
